import { useEffect, useState } from "react";
import { TooltipProvider } from "./ui/overlay";
import { Toaster } from "./ui/toaster";
import { LangProvider, useT } from "./i18n";
import { loadConfig, type ConsoleConfig } from "./lib/config";
import { StoreProvider, useStore } from "./store/store";
import { WalletProvider } from "./store/wallet";
import { Shell, navigate, useHashRoute } from "./components/Shell";
import { WalletOverlay } from "./components/WalletOverlay";
import { BuyContext } from "./components/common";
import { useMedia } from "./components/hooks";
import { BuyDialog, ConversationsPage } from "./pages/Conversations";
import { ConversationPage } from "./pages/Conversation";
import { ServicesPage, SettingsPage } from "./pages/Services";
import { ManagePage } from "./pages/Manage";
import { DeployPage } from "./pages/Deploy";
import { ExtensionManagePage, ExtensionPublishPage, ExtensionsPage } from "./pages/Extensions";
import { ExtensionRoute } from "./pages/ExtensionTab";

function Routes() {
  const [page, sub, third] = useHashRoute();
  const { theme } = useStore();
  const [buying, setBuying] = useState<string | null>(null);
  const narrow = useMedia("(max-width: 767px)");
  const known = page === "conversations" || page === "services" || page === "extensions" || page === "settings";
  useEffect(() => {
    if (!known) navigate("/conversations");
  }, [known]);
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [page, sub]);
  const active = known ? page! : "conversations";
  return (
    <BuyContext.Provider value={{ buying, onBuy: setBuying }}>
      <Shell active={active} activeSub={sub}>
        {active === "settings" && <SettingsPage />}
        {active === "services" &&
          (sub === "deploy" ? <DeployPage /> : sub ? <ManagePage name={sub} /> : <ServicesPage />)}
        {active === "conversations" && (sub ? <ConversationPage id={sub} /> : <ConversationsPage />)}
        {active === "extensions" &&
          (sub === "publish" ? (
            <ExtensionPublishPage />
          ) : sub && third === "manage" ? (
            <ExtensionManagePage id={sub} />
          ) : sub ? (
            <ExtensionRoute id={sub} />
          ) : (
            <ExtensionsPage />
          ))}
      </Shell>
      <WalletOverlay />
      {buying && <BuyDialog key={buying} token={buying} onClose={() => setBuying(null)} />}
      <Toaster
        theme={theme}
        position={narrow ? "top-center" : "top-right"}
        offset={{ top: 64, right: 16 }}
        mobileOffset={{ top: 64 }}
      />
    </BuyContext.Provider>
  );
}

function NoConfig() {
  const t = useT();
  return <p className="m-auto max-w-lg p-8 text-center text-sm text-muted-foreground">{t("config.missing")}</p>;
}

export function App() {
  const [config, setConfig] = useState<ConsoleConfig | null | undefined>(undefined);
  useEffect(() => {
    void loadConfig().then(setConfig);
  }, []);
  return (
    <LangProvider>
      {config === undefined ? null : config === null ? (
        <NoConfig />
      ) : (
        <StoreProvider config={config}>
          <WalletProvider>
            <TooltipProvider>
              <Routes />
            </TooltipProvider>
          </WalletProvider>
        </StoreProvider>
      )}
    </LangProvider>
  );
}
